/**
 * Main App component that orchestrates the application layout and handles global focus navigation.
 * Manages focus area switching between header and content, and integrates all major UI components.
 */
import { useState, useEffect } from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import type { WebContainer } from './deps/containers.types'
import { HeaderContainer } from './components/shell/HeaderContainer/HeaderContainer'
import { NameDialog } from './components/mateList/dialogs/NameDialog/NameDialog'
import { NameEntryForm } from './components/mateList/dialogs/NameEntryForm/NameEntryForm'
import { RoomSelectionFormContainer } from './components/shell/RoomSelectionForm/RoomSelectionFormContainer'
import { RoomCreationFormContainer } from './components/shell/RoomCreationForm/RoomCreationFormContainer'
import { PlayArea } from './components/playArea/PlayArea/PlayArea'
import { DebugPanelContainer } from './components/debugPanel/container/DebugPanelContainer'
import { MateListContainer } from './components/mateList/list/MateListContainer/MateListContainer'
import { GamelessContainer } from './components/gameless/GamelessContainer/GamelessContainer'
import { ProfilePickerDialog } from './components/mateList/dialogs/ProfilePickerDialog/ProfilePickerDialog'
import { RoomErrorView } from './components/shell/RoomErrorView/RoomErrorView'
import { useGameTheme } from './hooks/useGameTheme'
import { useFocusNavigationToggle } from './hooks/useFocusNavigationToggle'
import { useFocusNavigationDep, useControllerInputDep } from './components/providers/FocusNavigationProvider/FocusNavigationProvider'

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true
  return Boolean((el as any).isContentEditable)
}

function getURLInfo(mode: WebContainer['mode']) {
  if (typeof window === 'undefined') {
    return { isOmniMode: false, autoStartGameName: null, gameIdFromURL: null, stateNameFromURL: null }
  }
  const inOmniMode = mode.isOmniMode()
  
  // Extract game ID and state name from URL path
  // Format: /r/<roomName>/<gameId> or /r/<roomName>/<gameId>/<stateName> (trailing slash allowed)
  const pathMatch = window.location.pathname.match(/\/r\/[^\/]+\/([^\/]+)(?:\/([^\/]+))?\/?$/)
  const gameIdFromURL = pathMatch ? decodeURIComponent(pathMatch[1]) : null
  const stateNameFromURL = pathMatch?.[2] ? decodeURIComponent(pathMatch[2]) : null

  // Extract auto-start game name from URL hash or query params if in Omni mode
  // Format: /r/test:x#tictactoe or /r/test:x?game=tictactoe
  let autoStartGameName: string | null = null
  if (inOmniMode && !gameIdFromURL) {
    const hash = window.location.hash.slice(1) // Remove #
    const params = new URLSearchParams(window.location.search)
    autoStartGameName = hash || params.get('game') || null
  } else if (gameIdFromURL) {
    // If gameId is in URL, use it as autoStartGameName
    autoStartGameName = gameIdFromURL
  }

  return { isOmniMode: inOmniMode, autoStartGameName, gameIdFromURL, stateNameFromURL }
}

interface AppProps {
  container: WebContainer
}

export function RoomLoadingView() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(true)
    }, 3000)
    return () => clearTimeout(timer)
  }, [])

  if (!show) {
    return null
  }

  return (
    <div className="max-w-md w-full text-center mx-auto my-24 p-8 flex flex-col items-center justify-center">
      <div 
        className="w-12 h-12 border-4 border-solid border-muted/20 border-t-primary border-l-primary rounded-full animate-spin mb-6"
      ></div>
      <h3 className="text-lg font-bold text-muted animate-pulse uppercase tracking-wider">
        Connecting to Room...
      </h3>
    </div>
  )
}

function App({ container }: AppProps) {

  useSignals()
  useGameTheme() // Always apply theme CSS variables, even when no game is active
  
  const focusNavigation = useFocusNavigationDep()
  const controllerInput = useControllerInputDep()
  
  // Read shared screen mode signal reactively
  const isSharedScreen = container.mode.computedMode.value.sharedScreen
  const roomName = container.roomLink.roomName
  
  // Start local mates when in shared screen mode
  useEffect(() => {
    if (isSharedScreen) {
      container.localMatesSystem.localMates.start()
      // Controller input is already started automatically

      // Note: assignment preferences are keyed by (roomName, gameId) and are loaded by the
      // shared-screen start flow (roomActions) once a gameId is selected.
    } else {
      container.localMatesSystem.localMates.stop()
    }

    return () => {
      container.localMatesSystem.localMates.stop()
    }
  }, [isSharedScreen, roomName, container.localMatesSystem])
  
  // Enable focus navigation with 'F' key
  useFocusNavigationToggle(focusNavigation)
  
  // Handle menu button to toggle focus between header, content, and sidebar
  useEffect(() => {
    const unsubscribe = controllerInput.subscribe((event) => {
      if (event.type === 'menu') {
        if (!focusNavigation.isActive.value) {
          focusNavigation.enable()
        }
        // Toggle focus between header, content, and sidebar
        const current = focusNavigation.focusArea.value
        const isSidebarOpen = container.debugPanelVM.isOpen.value
        
        let next: 'header' | 'content' | 'dialog' | 'sidebar' = 'content'
        if (isSidebarOpen) {
          if (current === 'content') {
            next = 'sidebar'
          } else if (current === 'sidebar') {
            next = 'header'
          } else {
            next = 'content'
          }
        } else {
          next = current === 'header' ? 'content' : 'header'
        }
        focusNavigation.setFocusArea(next)
      }
    })
    return unsubscribe
  }, [controllerInput, focusNavigation, container.debugPanelVM])

  // Synchronize focusArea signal with pointer click targets
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      
      // 1. Sidebar/Debug panel click targets
      if (
        target.closest('[data-testid="debug-sidebar"]') || 
        target.closest('[data-testid="tool-content-area"]') || 
        target.closest('.w-\\[var\\(--debug-sidebar-width\\)\\]')
      ) {
        if (focusNavigation.focusArea.value !== 'sidebar') {
          focusNavigation.setFocusArea('sidebar')
        }
      }
      // 2. Header click targets
      else if (target.closest('header') || target.closest('[data-testid="header"]')) {
        if (focusNavigation.focusArea.value !== 'header') {
          focusNavigation.setFocusArea('header')
        }
      }
      // 3. Game/Play Area click targets
      else {
        if (focusNavigation.focusArea.value !== 'content') {
          focusNavigation.setFocusArea('content')
        }
      }
    }
    window.addEventListener('click', onClick, true)
    return () => window.removeEventListener('click', onClick, true)
  }, [focusNavigation])

  // Handle right bumper (togglePlayer event) to toggle players in Omni mode (same behavior as pressing 'A' key)
  // Room-wide (roomState.hasOmniMates, activeOmniMate.localOmniMateIds) - not this
  // client's own isOmniMode toggle or local mates - so any connected client can
  // cycle through every Omni mate in the room, not just the one that created them.
  useEffect(() => {
    const unsubscribe = controllerInput.subscribe((event) => {
      if (event.type === 'togglePlayer') {
        const omniMateIds = container.localMatesSystem.omniMates.activeOmniMate.localOmniMateIds.value
        if (container.roomState.hasOmniMates.value &&
            container.lobby.roomMode.value === 'room' &&
            (omniMateIds?.length ?? 0) >= 2) {
          if (typeof window !== 'undefined' && isTypingTarget(document.activeElement)) return
          container.localMatesSystem.omniMates.activeOmniMate.cycleLocalOmniMate()
        }
      }
    })
    return unsubscribe
  }, [controllerInput, container.localMatesSystem.omniMates.activeOmniMate, container.roomState, container.lobby])

  // Backtick (`) should always cycle through Omni mates, independent of emulation/controllers.
  useEffect(() => {
    if (typeof window === 'undefined') return

    const onKeyDown = (e: KeyboardEvent) => {
      // Use e.code (physical key) so backtick works on layouts where e.key is "Dead" or differs
      if (e.code !== 'Backquote') return
      if (e.repeat) return

      const omniMateIds = container.localMatesSystem.omniMates.activeOmniMate.localOmniMateIds.value
      if (!container.roomState.hasOmniMates.value) return
      if (container.lobby.roomMode.value !== 'room') return
      if ((omniMateIds?.length ?? 0) < 2) return
      if (isTypingTarget(document.activeElement)) return

      e.preventDefault()
      e.stopPropagation()
      container.localMatesSystem.omniMates.activeOmniMate.cycleLocalOmniMate()
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [container.localMatesSystem.omniMates.activeOmniMate, container.roomState, container.lobby])

  // Toggle debug panel using Alt+D (Option+D) or Ctrl+` (Control+Backtick), and capture screenshot using Cmd+P / Ctrl+P
  useEffect(() => {
    if (typeof window === 'undefined') return

    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(document.activeElement)) return

      const isAltD = e.altKey && e.code === 'KeyD'
      const isCtrlBacktick = e.ctrlKey && e.code === 'Backquote'
      const isCmdP = (e.metaKey || e.ctrlKey) && e.code === 'KeyP'

      if (isAltD || isCtrlBacktick) {
        e.preventDefault()
        e.stopPropagation()
        container.debugPanelVM.setIsOpen(!container.debugPanelVM.isOpen.value)
      } else if (isCmdP) {
        e.preventDefault()
        e.stopPropagation()
        if (!container.debugPanelVM.isOpen.value) {
          container.debugPanelVM.setIsOpen(true)
        }
        container.debugPanelVM.requestCaptureScreenshot()
      }
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [container.debugPanelVM])

  const { isOmniMode, autoStartGameName, stateNameFromURL } = getURLInfo(container.mode)
  const roomMode = container.lobby.roomMode
  const connectionStatus = container.roomConnection.status.value
  const hasError = !!container.roomConnection.error.value
  const showLoading = roomMode.value === 'room' && connectionStatus !== 'connected' && !hasError
  const showRoomLayout = roomMode.value === 'room' && connectionStatus === 'connected' && !hasError
  const isOwner = container.shellVM.room.isOwner.value
  const showClosedView = showRoomLayout && container.roomState.isClosed.value
  const showHeader = showRoomLayout && (!showClosedView || isOwner)
  const isGameless = showRoomLayout && container.shellVM.room.gameless.value

  return (
    <div className="app">
      {showHeader && (
        <HeaderContainer />
      )}

      <NameDialog
        localMates={container.localMatesSystem.localMates}
        auth={container.auth}
        video={container.video}
        videoEnabled={container.roomState.videoEnabled.value}
        isSharedScreen={isSharedScreen}
        isOwner={isOwner}
        deviceProfiles={container.localMatesSystem.deviceProfiles}
        connectedControllers={container.connectedControllers}
        getThumbnailHistory={container.roomActions.getThumbnailHistory}
        deleteThumbnailFromHistory={container.roomActions.deleteThumbnailFromHistory}
        updateRoom={container.roomConnection.updateRoom}
      />

      <ProfilePickerDialog
        deviceProfiles={container.localMatesSystem.deviceProfiles}
      />

      <div className="main-content">
        {showClosedView ? (
          <div className="game-area">
            <RoomErrorView
              error="This room is currently closed."
              isClosed
              opensAt={container.roomState.opensAt.value}
              onOpeningTimeReached={() => window.location.reload()}
              onBackToLobby={() => void container.shellVM.actions.leaveRoom()}
            />
          </div>
        ) : isGameless ? (
          <GamelessContainer />
        ) : (
          <div className="flex flex-1 min-h-0 min-w-0">
            {showRoomLayout && <MateListContainer />}
            <div className="game-area">
                {!isOmniMode && roomMode.value === 'name-entry' && (
                  <NameEntryForm
                    localMates={container.localMatesSystem.localMates}
                  />
                )}

                {!isOmniMode && roomMode.value === 'room-selection' && (
                  <RoomSelectionFormContainer
                    roomConnection={container.roomConnection}
                    lobby={container.lobby}
                    auth={container.auth}
                    localMates={container.localMatesSystem.localMates}
                  />
                )}

                {!isOmniMode && roomMode.value === 'room-creation' && (
                  <RoomCreationFormContainer
                    roomConnection={container.roomConnection}
                    lobby={container.lobby}
                    auth={container.auth}
                    localMates={container.localMatesSystem.localMates}
                  />
                )}

                {roomMode.value === 'room' && (
                  container.roomConnection.error.value ? (
                    <RoomErrorView
                      error={container.roomConnection.error.value}
                      isClosed={container.roomState.isClosed.value}
                      opensAt={container.roomState.opensAt.value}
                      onOpeningTimeReached={() => window.location.reload()}
                      onBackToLobby={() => {
                        container.roomConnection.clearError()
                        if (typeof window !== 'undefined') {
                          window.history.pushState(null, '', '/')
                          window.dispatchEvent(new PopStateEvent('popstate'))
                        }
                        container.lobby.roomMode.value = 'room-selection'
                      }}
                    />
                  ) : showLoading ? (
                    <RoomLoadingView />
                  ) : (
                    <PlayArea
                      autoStartGameName={autoStartGameName}
                      stateNameFromURL={stateNameFromURL}
                    />
                  )
                )}
            </div>
          </div>
        )}

        {showRoomLayout && !showClosedView && container.debugPanelVM.isOpen.value && (
          <DebugPanelContainer />
        )}
      </div>
    </div>
  )
}

export default App
